import {useEffect, useMemo, useRef, useState} from "react";
import {
  Link,
  Bookmark,
  Tag,
  Tags,
  Timer,
  FileUp,
  AlertCircle,
  X,
} from "lucide-react";
import {useVideoUpload} from "../../../hooks/useVideoUpload";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  getVideoLanguage,
} from "../utils/videoLanguage";
import {useYoutubeApi} from "../../../hooks/useYoutubeApi";
import CategoryCombobox from "../components/CategoryCombobox";
import {
  UploadType,
  isSaveReady,
  findDuplicateVideo,
  getFileSignature,
  uploadFailMessage,
} from "../utils/videoUploadValidation";

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;

const VideoPreview = ({
  previewUrl,
  uploadType,
  handleSave,
  handleCancel,
  showDelete,
  handleDelete,
  canSave,
  isSaving,
  isDuplicate,
  duplicateVideo,
}) => {
  const {getVideoIdFromUrl} = useYoutubeApi();

  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const ytContainerRef = useRef(null);
  const hlsRef = useRef(null);

  const [isPlayerReady, setIsPlayerReady] = useState(false);

  const showNoFilePreview = !previewUrl;
  const showFilePreview = !!previewUrl && uploadType === UploadType.FILE_UPLOAD;
  const showYouTubePreview =
    !!previewUrl && uploadType === UploadType.YOUTUBE_UPLOAD;

  // --- 1. INITIALIZATION EFFECT ---
  useEffect(() => {
    setIsPlayerReady(false);

    let cancelled = false;

    const cleanupPlayers = () => {
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
      // YT.Player replaces its target element with an iframe, so the
      // ytContainerRef div must be emptied between selections -- otherwise
      // the next player is built on a stale/replaced node and the preview
      // stays blank when switching videos (Khubaib bug).
      if (ytContainerRef.current) {
        ytContainerRef.current.replaceChildren();
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };

    if (uploadType === UploadType.YOUTUBE_UPLOAD) {
      const videoId = getVideoIdFromUrl(previewUrl);

      // The YouTube IFrame API must be present for the preview player to
      // render. On this page nothing else loads it, so window.YT stayed
      // undefined and loadYT polled forever — leaving the preview blank
      // (no thumbnail). Inject the script here, mirroring VideoYoutubePlayer.
      if (!window.YT) {
        const existingScript = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]',
        );
        if (!existingScript) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const firstScriptTag = document.getElementsByTagName("script")[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
      }

      const loadYT = () => {
        if (cancelled) return;
        if (!window.YT || !window.YT.Player || !ytContainerRef.current) {
          setTimeout(loadYT, 100);
          return;
        }

        cleanupPlayers();

        // Build the player on a fresh child div so YT.Player can safely
        // replace it with an iframe, leaving the stable wrapper intact for
        // the next selection.
        const playerElement = document.createElement("div");
        playerElement.style.width = "100%";
        playerElement.style.height = "100%";
        ytContainerRef.current.appendChild(playerElement);

        try {
          playerRef.current = new window.YT.Player(playerElement, {
            height: "100%",
            width: "100%",
            videoId: videoId,
            playerVars: {
              autoplay: 0,
              rel: 0,
              modestbranding: 1,
              controls: 0,
              disablekb: 1,
              enablejsapi: 1,
              origin: window.location.origin,
            },
            events: {
              onReady: () => {
                setIsPlayerReady(true);
              },
            },
          });
        } catch (e) {
          console.error(e);
        }
      };

      loadYT();
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      cleanupPlayers();

      if (
        previewUrl.includes(".m3u8") &&
        window.Hls &&
        window.Hls.isSupported()
      ) {
        const hls = new window.Hls();
        hls.loadSource(previewUrl);
        hls.attachMedia(videoElement);
        hlsRef.current = hls;
      } else {
        videoElement.src = previewUrl;
        setIsPlayerReady(true);
      }
    }
    return () => {
      cancelled = true;
      cleanupPlayers();
    };
  }, [uploadType, previewUrl, getVideoIdFromUrl]);

  // --- 2. PAUSE WHEN ANOTHER VIDEO STARTS PLAYING ---
  useEffect(() => {
    if (!isPlayerReady) return;

    if (
      uploadType === UploadType.YOUTUBE_UPLOAD &&
      playerRef.current?.pauseVideo
    ) {
      playerRef.current.pauseVideo();
    } else if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [isPlayerReady, uploadType, previewUrl]);

  return (
    <div className="flex flex-col gap-4 shrink-0 w-full">
      <div className="w-full aspect-video bg-slate-100 rounded-3xl overflow-hidden border border-slate-200 flex items-center justify-center">
        {showNoFilePreview && (
          <div className="text-center text-slate-400">
            <FileUp size={64} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No video selected</p>
            <p className="text-sm mt-2">
              Upload a file or enter a YouTube URL to preview
            </p>
          </div>
        )}
        {showFilePreview && (
          <video
            key="file-player"
            ref={videoRef}
            className="w-full h-full object-contain"
            controls
          />
        )}
        {showYouTubePreview && (
          <div key="youtube-wrapper" className="w-full h-full min-h-[400px]">
            <div ref={ytContainerRef} className="w-full h-full" />
          </div>
        )}
      </div>

      {isDuplicate && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-700 font-semibold">
          <AlertCircle size={20} className="shrink-0" />
          <p>
            This video is already in the library
            {duplicateVideo?.title ? ` as “${duplicateVideo.title}”` : ""}.
          </p>
        </div>
      )}

      <div className="flex flex-row gap-4">
        {showDelete && (
          <button
            onClick={handleDelete}
            disabled={!previewUrl}
            className="flex-1 px-6 py-3 rounded-xl bg-rose-100 text-rose-700 font-semibold text-lg hover:bg-rose-200 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            Delete Video
          </button>
        )}
        <button
          onClick={handleCancel}
          className="flex-1 px-6 py-3 rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold text-lg hover:bg-slate-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!previewUrl || !canSave || isSaving || isDuplicate}
          title={
            !previewUrl
              ? "Add a video URL first"
              : isDuplicate
                ? "This video is already in the library"
                : !canSave
                  ? "Fill in title, category, and wait for video length to load"
                  : undefined
          }
          className="flex-1 px-6 py-3 rounded-xl bg-indigo-500 text-white font-semibold text-lg hover:bg-indigo-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving\u2026" : "Save Video"}
        </button>
      </div>
    </div>
  );
};

const DEFAULT_THUMBNAIL = "./images/default_thumbnail.png";

const getListThumbnail = (url) => {
  if (!url) return DEFAULT_THUMBNAIL;
  const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
  if (!isYouTube) return DEFAULT_THUMBNAIL;
  const id = url.includes("v=")
    ? url.split("v=")[1].split("&")[0]
    : url.split("/").pop().split("?")[0];
  return id
    ? `https://img.youtube.com/vi/${id}/mqdefault.jpg`
    : DEFAULT_THUMBNAIL;
};

const VideoListView = ({videos, onVideoSelect, selectedVideoID}) => {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-2">
      <ul className="space-y-2">
        {videos.map((video) => {
          const selected = selectedVideoID === video.id;

          return (
            <li
              key={video.id}
              onClick={() => onVideoSelect(video)}
              className={`
                group cursor-pointer rounded-2xl border px-4 py-3 transition-all
                ${
                  selected
                    ? "border-indigo-200 bg-indigo-50 shadow-sm"
                    : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                }
              `}
            >
              <div className="flex items-center gap-4">
                <img
                  src={getListThumbnail(video.url)}
                  alt=""
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_THUMBNAIL;
                  }}
                  className="h-12 w-20 shrink-0 rounded-lg object-cover bg-slate-100"
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {video.title}
                  </p>

                  <p className="mt-1 truncate text-sm text-slate-500">
                    {video.category}
                  </p>
                </div>

                <span className="shrink-0 w-[72px] text-right text-sm text-slate-500 tabular-nums">
                  {formatTime(video.videoLength)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const UploadFormFileInput = ({selectedFile, setSelectedFile}) => {
  const fileInputRef = useRef(null);
  const [fileUploadError, setFileUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (fileInputRef.current && !selectedFile) {
      fileInputRef.current.value = "";
    } else {
      setFileUploadError("");
    }
  }, [selectedFile]);

  const validateAndSetFile = (file) => {
    setFileUploadError("");

    if (!file) return;

    const isMp4 =
      file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");

    if (!isMp4) {
      setSelectedFile(null);
      setFileUploadError("Invalid file format. Please upload an MP4 file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileUploadError("File is too large. Maximum file size is 2GB.");
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (size) => {
    if (!size) return "0 MB";
    const sizeInMb = size / (1024 * 1024);
    if (sizeInMb >= 1024) {
      return `${(sizeInMb / 1024).toFixed(2)} GB`;
    }
    return `${sizeInMb.toFixed(2)} MB`;
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,.mp4"
        onChange={handleFileInputChange}
        className="hidden"
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`w-full min-h-[280px] rounded-3xl border-2 border-dashed flex flex-col items-center justify-center px-6 py-10 transition-all ${
          isDragging
            ? "border-indigo-400 bg-indigo-50/60"
            : "border-slate-200 bg-slate-50/40"
        }`}
      >
        {selectedFile ? (
          <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-5 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
              <FileUp size={24} />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 truncate">
                {selectedFile.name}
              </p>
              <p className="text-sm text-slate-400">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>

            <button
              type="button"
              onClick={handleRemoveFile}
              className="w-9 h-9 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center hover:bg-rose-100"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <>
            <FileUp size={54} className="text-slate-300 mb-6" />

            <p className="text-xl font-medium text-slate-400 mb-3 text-center">
              Drag and drop your MP4 file to upload
            </p>

            <p className="text-sm md:text-base text-slate-300 font-medium mb-8 text-center">
              Max file size: 2GB. Recommended: H.264, 1080p.
            </p>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-8 py-3 rounded-xl border border-slate-300 bg-white text-slate-400 font-semibold text-lg hover:bg-slate-50 transition-colors"
            >
              Select File
            </button>
          </>
        )}
      </div>

      {fileUploadError && (
        <div className="flex items-center gap-3 mt-5 text-rose-400 font-semibold">
          <AlertCircle size={20} />
          <p>{fileUploadError}</p>
        </div>
      )}
    </>
  );
};

const UploadFormTextInput = ({
  className,
  title,
  placeholder,
  value,
  onChange,
  disabled,
  iconClass: Icon,
  autocompleteOptions,
}) => {
  const normalized = title
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

  const datalistId = `${normalized}-autocomplete`;

  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-slate-800 ml-1">
        {title}
      </label>
      <div className="relative">
        <Icon
          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          size={18}
        />
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          list={datalistId}
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-3xl outline-none text-slate-900 [&::-webkit-calendar-picker-indicator]:hidden"
        />
        <datalist id={datalistId}>
          {autocompleteOptions?.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      </div>
    </div>
  );
};

const UploadHeader = ({uploadType, setUploadType, handleCancel}) => {
  const handleOnYoutubeClick = () => {
    handleCancel();
    setUploadType(UploadType.YOUTUBE_UPLOAD);
  };

  const handleOnFileClick = () => {
    handleCancel();
    setUploadType(UploadType.FILE_UPLOAD);
  };

  return (
    <div
      className={
        "flex flex-col gap-4 w-full mb-6 sm:flex-row sm:items-center sm:justify-between"
      }
    >
      <div className={"flex flex-col items-begin justify-center gap-4"}>
        <h2 className="text-xl font-semibold text-[#262626] tracking-tight">
          Upload Content
        </h2>
        <p className="text-[#262626]/50 text-sm font-medium">
          Choose your preferred method to add a video to the library.
        </p>
      </div>
      <div
        className={
          "flex flex-row items-stretch gap-2 sm:ml-auto p-2 bg-[#e6edf4] max-w-64 rounded-md"
        }
      >
        <button
          className={
            "px-4 py-2 rounded" +
            (uploadType === UploadType.YOUTUBE_UPLOAD ? " bg-white" : "")
          }
          onClick={handleOnYoutubeClick}
        >
          YouTube URL
        </button>
        <button
          className={
            "px-4 py-2 rounded" +
            (uploadType === UploadType.FILE_UPLOAD ? " bg-white" : "")
          }
          onClick={handleOnFileClick}
        >
          File URL
        </button>
      </div>
    </div>
  );
};

const formatTime = (totalSeconds) => {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  if (hrs) {
    return [hrs, mins, secs].map((v) => String(v).padStart(2, "0")).join(":");
  }
  return [mins, secs].map((v) => String(v).padStart(2, "0")).join(":");
};

const UploadForm = ({
  uploadType,
  selectedFile,
  setSelectedFile,
  formData,
  setFormData,
  categories,
  videoId,
  selectedVideo,
}) => {
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({...prev, [field]: value}));
  };

  return (
    <div className="flex-1 flex flex-col gap-4 w-full">
      <div className="flex flex-col items-stretch gap-2 w-full">
        {uploadType === UploadType.YOUTUBE_UPLOAD && (
          <>
            <UploadFormTextInput
              title="YouTube Video URL"
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full"
              iconClass={Link}
              disabled={!!selectedVideo}
              value={formData.youtubeUrl}
              onChange={(e) => handleInputChange("youtubeUrl", e.target.value)}
            />
            <div className="flex flex-row justify-between gap-4 w-full">
              <UploadFormTextInput
                title="Video ID"
                placeholder="Auto-generated"
                className="flex-1"
                iconClass={Bookmark}
                disabled={true}
                value={videoId}
              />
              <UploadFormTextInput
                title="Video Length"
                placeholder="00:00:00"
                className="flex-1"
                iconClass={Timer}
                disabled={true}
                value={formatTime(formData.videoLength)}
                onChange={(e) =>
                  handleInputChange("videoLength", e.target.value)
                }
              />
            </div>
          </>
        )}
        {uploadType === UploadType.FILE_UPLOAD && (
          <div className="w-full mt-6">
            <UploadFormFileInput {...{selectedFile, setSelectedFile}} />
          </div>
        )}
        <UploadFormTextInput
          title="Video Title"
          placeholder="Enter video title"
          className="w-full"
          iconClass={Tag}
          disabled={uploadType === UploadType.YOUTUBE_UPLOAD}
          value={formData.videoTitle}
          onChange={(e) => handleInputChange("videoTitle", e.target.value)}
        />

        <CategoryCombobox
          title="Category"
          placeholder="Enter category"
          className="w-full"
          iconClass={Tags}
          disabled={false}
          value={formData.category}
          options={categories}
          onChange={(e) => handleInputChange("category", e.target.value)}
        />

        {/* Language drives the learner-facing EN/ES switch. Category must stay
            in English regardless of this value -- it is a functional key that
            the section grouping and the orientation/CRM rules match on, not a
            label. */}
        <div className="w-full mt-6">
          <label
            htmlFor="video-language"
            className="block text-sm font-semibold text-slate-700 mb-2"
          >
            Language
          </label>
          <select
            id="video-language"
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            value={formData.language}
            onChange={(e) => handleInputChange("language", e.target.value)}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

const getVideoDuration = (url, setDurationCallback) => {
  const video = document.createElement("video");
  video.preload = "metadata";

  video.onloadedmetadata = () => {
    window.URL.revokeObjectURL(video.src);
    setDurationCallback(Math.trunc(video.duration)); // Тривалість у секундах
  };

  video.src = url;
};

// The pristine form. Shared by the initial state and the reset in
// handleCancel so the two cannot drift -- they had, twice: the reset carried a
// dead `videoId` key (the real one is derived by useMemo, never read off
// formData) and was missing `language`. That omission dropped the key from the
// object, which both flipped the language <select> from controlled to
// uncontrolled -- it kept displaying the old choice -- and sent `undefined` to
// createVideo, where JSON.stringify omits it and a row with no language reads
// back as English. An admin could pick Espanol, cancel, then save the next
// video as English while the selector still read Espanol.
const EMPTY_FORM = {
  videoUrl: "",
  youtubeUrl: "",
  videoLength: "",
  videoTitle: "",
  category: "",
  // Catalog language. Defaults to English so the common case is one less
  // decision, and so a row saved without touching this field matches every
  // pre-existing video.
  language: DEFAULT_LANGUAGE,
};

const VideoUpload = () => {
  const {
    videos,
    categories,
    loadVideos,
    createVideo,
    createVideoUpload,
    updateVideo,
    deleteVideo,
  } = useVideoUpload();
  const {getVideoIdFromUrl, getVideoInfo} = useYoutubeApi();

  const [uploadType, setUploadType] = useState(UploadType.YOUTUBE_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const videoId = useMemo(
    () => getVideoIdFromUrl(formData.youtubeUrl),
    [formData.youtubeUrl, getVideoIdFromUrl],
  );

  useEffect(() => {
    if (uploadType !== UploadType.FILE_UPLOAD || !selectedFile) return;

    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    setFormData((f) => ({
      ...f,
      videoUrl: "",
      videoTitle: selectedFile.name.split(".")[0] || "",
    }));
    getVideoDuration(url, (duration) => {
      setFormData((f) => ({...f, videoLength: duration}));
    });

    return () => URL.revokeObjectURL(url);
  }, [uploadType, selectedFile, setFormData, setPreviewUrl]);

  useEffect(() => {
    if (uploadType !== UploadType.YOUTUBE_UPLOAD || !formData.youtubeUrl)
      return;
    setPreviewUrl(formData.youtubeUrl);
  }, [uploadType, formData.youtubeUrl, setPreviewUrl]);

  useEffect(() => {
    if (uploadType !== UploadType.YOUTUBE_UPLOAD || !videoId) return;
    let canceled = false;

    getVideoInfo(videoId).then((data) => {
      if (canceled || !data) return;
      setFormData((f) => ({
        ...f,
        videoTitle: data.title,
        videoLength: data.duration,
      }));
    });

    return () => {
      canceled = true;
    };
  }, [videoId, setFormData, getVideoInfo, uploadType]);

  const [isSaving, setIsSaving] = useState(false);
  const canSave = isSaveReady(
    formData,
    uploadType,
    selectedVideo,
    selectedFile,
  );

  // Flag uploads of a video that already exists in the library. Only
  // applies to new rows (editing an existing row just mutates category).
  // YouTube rows are matched by video id so different URL forms collide;
  // file rows are matched by name + size (the picked file has no url yet).
  const fileSignature =
    uploadType === UploadType.FILE_UPLOAD ? getFileSignature(selectedFile) : "";
  const duplicateVideo =
    selectedVideo?.id === undefined
      ? findDuplicateVideo(
          videos,
          uploadType === UploadType.YOUTUBE_UPLOAD
            ? formData.youtubeUrl
            : formData.videoUrl,
          fileSignature,
        )
      : null;
  const isDuplicate = !!duplicateVideo;

  const handleSave = async () => {
    // Defence in depth: the button is disabled when !canSave, but
    // guarding here keeps a stale render or keyboard activation from
    // persisting an incomplete row (Khubaib bug #1).
    if (!canSave || isSaving) return;

    // Block re-uploading a video that is already in the library
    // (Khubaib bug: duplicate uploads). The Save button is also
    // disabled, but guard here against stale renders / keyboard saves.
    if (isDuplicate) {
      window.alert(
        `This video is already in the library${
          duplicateVideo?.title ? ` as "${duplicateVideo.title}"` : ""
        }.`,
      );
      return;
    }

    setIsSaving(true);
    const finish = () => {
      setIsSaving(false);
      handleCancel();
      loadVideos();
    };
    const fail = (err) => {
      console.error(err);
      setIsSaving(false);
      window.alert(uploadFailMessage(uploadType, err));
    };

    try {
      if (selectedVideo?.id === undefined) {
        let finalUrl =
          uploadType === UploadType.YOUTUBE_UPLOAD
            ? formData.youtubeUrl.trim()
            : formData.videoUrl.trim();

        if (uploadType === UploadType.FILE_UPLOAD) {
          if (!selectedFile) {
            throw new Error("Please select an MP4 file before saving.");
          }
          let upload;
          try {
            upload = await createVideoUpload({
              fileName: selectedFile.name,
              contentType: selectedFile.type || "video/mp4",
            });
          } catch (e) {
            throw new Error(
              "Could not start the upload. Please try again in a moment.",
            );
          }

          let putRes;
          try {
            putRes = await fetch(upload.uploadURL, {
              method: "PUT",
              headers: {
                "Content-Type": upload.contentType || "video/mp4",
              },
              body: selectedFile,
            });
          } catch (e) {
            // A network error / aborted PUT (e.g. a large file outrunning
            // the presigned URL window) lands here rather than on !ok.
            throw new Error(
              "The video file could not be uploaded. Check your connection and try again.",
            );
          }

          if (!putRes.ok) {
            throw new Error(
              "The video file could not be uploaded. Check your connection and try again.",
            );
          }

          finalUrl = upload.assetUrl;
        }

        await createVideo({
          title: formData.videoTitle.trim(),
          category: formData.category.trim(),
          url: finalUrl,
          // Coerce to Number so the backend never receives an empty
          // string -- previously this could be "" if a YouTube row was
          // saved before getVideoInfo resolved.
          videoLength: Number(formData.videoLength),
          language: formData.language,
          // Persist a name+size signature for file uploads so a later
          // re-upload of the same file is detected as a duplicate (the
          // randomUUID S3 url alone never collides).
          ...(uploadType === UploadType.FILE_UPLOAD && fileSignature
            ? {fileSignature}
            : {}),
        });
        finish();
      } else {
        await updateVideo({
          id: selectedVideo.id,
          category: formData.category.trim(),
          // The selector is live when editing, so the chosen language has to
          // reach the row -- otherwise switching a video to Espanol appears
          // to work and is silently dropped on save.
          language: formData.language,
        });
        finish();
      }
    } catch (err) {
      fail(err);
    }
  };

  const handleDelete = () => {
    const confirm = window.confirm("Delete this video?");
    if (selectedVideo && !!selectedVideo.id && confirm) {
      deleteVideo(selectedVideo.id).then(() => {
        handleCancel();
        loadVideos();
      });
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setSelectedVideo(null);
    setPreviewUrl("");
    setFormData(EMPTY_FORM);
  };

  const handleVideoSelected = (video) => {
    setSelectedVideo(video);
    setPreviewUrl(video.url);
    setUploadType(
      video.url?.includes("youtube.com") || video.url?.includes("youtu.be")
        ? UploadType.YOUTUBE_UPLOAD
        : UploadType.FILE_UPLOAD,
    );
    setFormData({
      ...formData,
      videoUrl: video.url,
      youtubeUrl: video.url,
      videoTitle: video.title,
      category: video.category,
      videoLength: video.videoLength,
      // Seed from the row being edited, so the selector reflects the video
      // on screen rather than whatever the form was last left on.
      language: getVideoLanguage(video),
    });
  };

  return (
    <div className={"flex flex-col gap-4 lg:h-full lg:min-h-0 lg:flex-row"}>
      <div
        className={
          "flex flex-col items-center gap-4 lg:flex-[2] lg:min-h-0 lg:min-w-0 lg:justify-start lg:overflow-y-auto"
        }
      >
        <UploadHeader {...{uploadType, setUploadType, handleCancel}} />
        <UploadForm
          {...{
            selectedFile,
            setSelectedFile,
            uploadType,
            formData,
            setFormData,
            categories,
            videoId,
            selectedVideo,
          }}
        />
      </div>
      <div className="flex flex-col gap-4 min-h-0 lg:flex-[1] lg:min-w-0 lg:h-full">
        <VideoPreview
          {...{
            selectedFile,
            previewUrl,
            uploadType,
            handleSave,
            handleCancel,
            handleDelete,
            canSave,
            isSaving,
            isDuplicate,
            duplicateVideo,
          }}
          showDelete={!!selectedVideo}
        />
        <VideoListView
          videos={videos}
          onVideoSelect={handleVideoSelected}
          selectedVideoID={selectedVideo?.id}
        />
      </div>
    </div>
  );
};

export default VideoUpload;
